<template>
  <div
    ref="wrapperRef"
    v-click-outside="closeDropdown"
    :class="['flex', { 'md:relative': direction === 'up' }]"
  >
    <slot :on-click="toggleDropdown" name="toggle"></slot>
    <div
      v-if="!$slots.toggle"
      :class="[
        'relative flex h-8 w-8 cursor-pointer flex-row justify-center rounded-lg ',
        {
          'bg-light-2 dark:bg-dark-4': isDropdownOpen && !disableSelected,
          'hover:bg-light-2 hover:dark:bg-dark-4': !disableHover,
        },
      ]"
      :data-testid="testId"
      @click="toggleDropdown"
    >
      <em :class="['mdi', 'text-2xl', toggleIcon]" />
    </div>

    <div
      v-if="isDropdownOpen"
      ref="panelRef"
      :class="[
        width,
        'absolute z-40 flex flex-col justify-center rounded-lg bg-light-2 shadow dark:bg-dark-4 ',
        {
          'translate-y-[45px] md:bottom-full md:-translate-y-2':
            direction === 'up',
        },
        { 'translate-y-10': direction === 'down' },
        {
          '-translate-x-[calc(100%-39px)] md:translate-x-0':
            direction === 'right',
        },
        { '-translate-x-[calc(100%-40px)]': directionX === 'left' },
      ]"
      :style="translateX"
    >
      <ul class="dropdown-container m-0 list-none px-0 py-1 text-sm not-prose">
        <slot></slot>
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, inject, nextTick, onMounted, ref } from 'vue';

interface Props {
  icon: string;
  direction: string;
  directionX: string;
  width: string;
  disableHover?: boolean;
  disabledSelected?: boolean;
}

export default {
  props: {
    icon: {
      type: String,
      default: '',
    },
    direction: {
      type: String,
      default: 'down',
    },
    directionX: {
      type: String,
      default: 'right',
    },
    width: {
      type: String,
      default: 'min-w-52 w-max',
    },
    disableHover: {
      type: Boolean,
      default: false,
    },
    disableSelected: {
      type: Boolean,
      default: false,
    },
    testId: {
      type: String,
      default: 'optionsDropdown',
    },
  },
  setup(props: Props) {
    const scrollPositionRef = inject(
      'scrollPositionRef',
      ref<HTMLElement | null>(null)
    );
    const scrollPosition = ref(0);
    const isDropdownOpen = ref<boolean>(false);

    const wrapperRef = ref<HTMLElement | null>(null);
    const panelRef = ref<HTMLElement | null>(null);
    const translateAmount = ref<number>(0);

    const currentDirectionX = ref(props.directionX || 'right');

    const getScrollPosition = () => {
      isDropdownOpen.value = false;
      scrollPosition.value = scrollPositionRef.value?.scrollLeft || 0;
    };

    onMounted(() => {
      scrollPositionRef.value?.addEventListener('scroll', getScrollPosition);
    });

    const translateX = computed(() => {
      return `--tw-translate-x: -${translateAmount.value}px !important`;
    });

    const closeDropdown = () => {
      isDropdownOpen.value = false;
    };
    const toggleDropdown = async () => {
      isDropdownOpen.value = !isDropdownOpen.value;
      if (isDropdownOpen.value) {
        await nextTick();

        const wrapper = wrapperRef.value;
        const panel = panelRef.value;
        if (wrapper && panel) {
          const wrapperRect = wrapper.getBoundingClientRect();
          const panelWidth = panel.offsetWidth || 0;
          const spaceRight = window.innerWidth - wrapperRect.right;
          currentDirectionX.value = spaceRight < panelWidth ? 'left' : 'right';

          if (
            spaceRight < panelWidth &&
            panelRef.value?.getBoundingClientRect().width
          ) {
            translateAmount.value =
              scrollPosition.value +
              panelRef.value?.getBoundingClientRect().width -
              32;
            return;
          }

          translateAmount.value = scrollPosition.value;
        }
      }
    };

    return {
      scrollPositionRef,
      scrollPosition,
      isDropdownOpen,
      translateX,
      closeDropdown,
      toggleDropdown,
      toggleIcon: props.icon,
      wrapperRef,
      panelRef,
      currentDirectionX,
    };
  },
};
</script>
